// CLARTEXT · Abschlussseite 2.0 · UI-V3
// Zwei Entscheidungskarten ("Türen") statt Vergleichstabelle — Struktur und
// Copy aus dem verbindlichen Betreiber-Mockup (landingpage_mockup_v2.html).
// Reihenfolge (Betreiber 09.09.): featured Abo-Karte ZUERST (oben auf Mobile,
// links auf Desktop), Download-Karte danach. Zwei-Türen-Logik UNVERÄNDERT:
// exakt zwei gleichwertige Optionen, gleiche Handler, gleiche Test-IDs
// (abschluss-v2-one-time-cta-top / -trial-cta-top).
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  DOOR_DOWNLOAD_PUNKTE,
  PREIS_SUFFIX_KOPF,
  CTA_KAUF_LABEL,
  CTA_ABO_LABEL,
  TRIAL_HINT_KURZ,
  FORK_INTRO_TITEL,
  FORK_INTRO_TEXT,
  DOOR_DOWNLOAD,
  DOOR_TRIAL,
  DOOR_TRIAL_KURZ,
  DETAILS_TOGGLE_LABEL,
  RISK_NOTE,
} from "@/pages/abschluss/data";

import FirmennameDialog from "./FirmennameDialog";

export function AbschlussForkIntro() {
  return (
    <section className="bridge" data-testid="abschluss-v2-fork-intro">
      <div className="wrap">
        <h2 className="reveal">{FORK_INTRO_TITEL}</h2>
        <p className="reveal d1">{FORK_INTRO_TEXT}</p>
      </div>
    </section>
  );
}

// Einklappbarer "Was das konkret heißt"-Bereich der Download-Karte
// (Betreiber-Copy 09.09.: 10 eigenständige Punkte statt Tabellen-Spalte).
function DoorDetails({ testId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="details-toggle"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {DETAILS_TOGGLE_LABEL} {open ? "↑" : "↓"}
      </button>
      {open && (
        <div className="detail-list" data-testid={testId}>
          {DOOR_DOWNLOAD_PUNKTE.map((punkt) => (
            <div className="detail-row" key={punkt.label}>
              <span className="mark" aria-hidden="true">→</span>
              <span>
                <strong>{punkt.label}:</strong> {punkt.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function EntscheidungsKarten({
  priceText,
  companyName,
  setCompanyName,
  companyChoice,
  setCompanyChoice,
  choicePromptTick,
  loading,
  error,
  buy,
  meldeAboKnopf, // MESS-10c, Betreiberfreigabe 03.11.2026
}) {
  const firmennameRef = useRef(null);
  const [flash, setFlash] = useState(false);
  // B1 (Juni 2026): Tür A startet den Trial NICHT mehr direkt, sondern führt
  // auf die künftige Abo-Seite /abo (dort liegt der eindeutige Trial-CTA).
  const goToAbo = () => {
    // MESS-10c, Betreiberfreigabe 03.11.2026: Meldung zuerst; sie darf den Seitenwechsel nie verzögern oder verhindern.
    try {
      if (meldeAboKnopf) meldeAboKnopf();
    } catch {
      /* Seitenwechsel immer */
    }
    window.location.href = "/abo";
  };

  // Teil 4 (22.09.2026): Klick auf „Herunterladen" ohne Ja/Nein-Wahl → sanft zur
  // Auswahl scrollen + kurzer Hervorhebungs-Puls. Die Fehlerbox bleibt zusätzlich.
  useEffect(() => {
    if (!choicePromptTick) return undefined;
    const el = firmennameRef.current;
    if (!el) return undefined;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1300);
    return () => clearTimeout(t);
  }, [choicePromptTick]);

  return (
    <>
      <section
        className="doors-sect"
        data-testid="abschluss-v2-doors"
        aria-label="Zwei Wege: CLartext übernimmt oder Einmaliger Download"
      >
        <div className="wrap doors">
          {/* Tür 1 — CLARTEXT (featured, zuerst — oben auf Mobile, links auf
              Desktop). B1: kurze Karte, primärer CTA führt auf /abo; KEIN
              direkter Trial-Start, KEINE 7-Punkte-Liste, KEIN Abo-Preis. */}
          <div
            className="door featured reveal in"
            data-testid="abschluss-v2-door-trial"
          >
            <div data-testid="proto2-kopf-clartext">
              <div className="door-eyebrow">{DOOR_TRIAL.eyebrow}</div>
              <h3>{DOOR_TRIAL.titel}</h3>
              <p className="emotion">{DOOR_TRIAL_KURZ}</p>

              <button
                type="button"
                className="p-btn cta-green"
                data-testid="abschluss-v2-trial-cta-top"
                onClick={goToAbo}
              >
                {CTA_ABO_LABEL}
              </button>
              <div className="p-meta">{TRIAL_HINT_KURZ}</div>
            </div>
          </div>

          {/* Tür 2 — Einmaliger Download */}
          <div className="door reveal in" data-testid="abschluss-v2-door-download">
            <div data-testid="proto2-kopf-download">
              <div className="door-eyebrow">{DOOR_DOWNLOAD.eyebrow}</div>
              <h3>{DOOR_DOWNLOAD.titel}</h3>
              <p className="emotion">{DOOR_DOWNLOAD.emotion}</p>

              <div
                ref={firmennameRef}
                data-testid="firmenname-flash-wrap"
                className={flash ? "firmenname-flash" : undefined}
              >
                <FirmennameDialog
                  testId="firmenname-dialog"
                  choice={companyChoice}
                  onChoice={setCompanyChoice}
                  companyName={companyName}
                  onCompanyNameChange={setCompanyName}
                />
              </div>

              {error && (
                <p
                  data-testid="abschluss-v2-buy-error"
                  role="alert"
                  className="buy-error"
                >
                  {error}
                </p>
              )}

              <button
                type="button"
                className="p-btn ghost"
                data-testid="abschluss-v2-one-time-cta-top"
                onClick={buy}
                disabled={loading}
                aria-busy={loading}
                style={{ opacity: loading ? 0.75 : 1 }}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="spin" aria-hidden="true" />
                    &nbsp;Weiterleitung zu Stripe …
                  </>
                ) : (
                  CTA_KAUF_LABEL
                )}
              </button>
              <div className="p-meta">
                {priceText ? `Einmaliger Kauf · ${priceText}` : PREIS_SUFFIX_KOPF}
              </div>

              <DoorDetails testId="abschluss-v2-door-download-details" />
            </div>
          </div>
        </div>
      </section>

      <div className="risk-note wrap" data-testid="abschluss-v2-risk-note">
        {RISK_NOTE}
      </div>
    </>
  );
}
